/**
 * Jovii — localStorage, safely.
 *
 * Safari in private mode throws on `localStorage` access, and a corrupted value
 * must never take the app down. Every read has a default; every write is
 * best-effort. // IOS-UNVERIFIED — the private-mode throw is handled but untested
 * on a real device.
 */

/** All keys are namespaced and versioned so a schema change is a new key. */
export const KEYS = {
  prefs: 'jt.prefs.v1',
  progress: 'jt.progress.v1',
  recent: 'jt.recent.v1',
  rooms: 'jt.rooms.v1',
  /** Added by the SHELL agent: the things My Jovii keeps for you. */
  saved: 'jt.saved.v1',
  /** The thumb on a title page. Per browser, read by nothing but the control. */
  rated: 'jt.rated.v1',
  /** The downloads index — what is on this device, and how far each one got. */
  downloads: 'jt.downloads.v1',
  /** Added by the CAST lane: resolved cast rows, per title (docs/CAST.md). */
  cast: 'jt.cast.v1',
  /** The optional TMDB key — the SubDL pattern: absent = the source is skipped. */
  tmdbKey: 'jt.tmdbKey.v1'
} as const;

let available: boolean | null = null;

function store(): Storage | null {
  if (available === false) return null;
  try {
    const s = window.localStorage;
    if (available === null) {
      const probe = '__jt_probe__';
      s.setItem(probe, '1');
      s.removeItem(probe);
      available = true;
    }
    return s;
  } catch {
    available = false;
    return null;
  }
}

export function readJson<T>(key: string, fallback: T, decode?: (value: unknown) => T): T {
  const s = store();
  if (!s) return fallback;
  try {
    const raw = s.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed == null ? fallback : decode ? decode(parsed) : parsed;
  } catch {
    // A corrupt entry is worse than no entry: drop it so it cannot fail twice.
    try {
      s.removeItem(key);
    } catch {
      /* nothing left to try */
    }
    return fallback;
  }
}

export function writeJson(key: string, value: unknown): void {
  const s = store();
  if (!s) return;
  try {
    s.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded, or private mode: preferences are not worth an exception */
  }
}

export function remove(key: string): void {
  const s = store();
  if (!s) return;
  try {
    s.removeItem(key);
  } catch {
    /* see above */
  }
}
